<script setup lang="ts">
import { useMortality } from "@/composables/tasks/mortality-check/useMortality";
import { onMounted, ref, watch, computed } from "vue";
import FooterActions from "@/components/v2/Tasks/Vaccination/Buttons/ActionButtons/FooterActions.vue";

const { activeTank, activeGroup, navigateToTankView, updateGroupMortalityData } = useMortality();
const mortalityReasons = ["Dead", "EHE"];

const quantity = ref(1);
const selectedReason = ref("");
const comment = ref("");

const totalFishCount = computed(() =>
    activeGroup.value?.amountInTank || activeGroup.value?.count || 0
);

const totalMortality = computed(() => {
    if (!activeGroup.value?.progressData) return 0;

    if (Array.isArray(activeGroup.value.progressData)) {
        return activeGroup.value.progressData.reduce(
            (acc, item) => acc + (item.quantity || 0), 0
        );
    } else if (typeof activeGroup.value.progressData === 'object') {
        return activeGroup.value.progressData.quantity || 0;
    }

    return 0;
});

const mortalityRate = computed(() => {
    if (totalFishCount.value === 0) return "0.00";
    const rate = (totalMortality.value / (totalFishCount.value + totalMortality.value)) * 100;
    return rate.toFixed(2);
});

const increaseQuantity = () => quantity.value++;
const decreaseQuantity = () => { if (quantity.value > 1) quantity.value--; };
const handleReason = (reason: string) => selectedReason.value = reason;

const getLatestProgressEntry = (progressData: unknown): {
    quantity?: number;
    reason?: string;
    comment?: string;
} | null => {
    if (!progressData) return null;

    if (Array.isArray(progressData)) {
        if (progressData.length === 0) return null;
        return progressData[progressData.length - 1] as {
            quantity?: number;
            reason?: string;
            comment?: string;
        };
    }

    if (typeof progressData === "object") {
        return progressData as {
            quantity?: number;
            reason?: string;
            comment?: string;
        };
    }

    return null;
};

onMounted(() => {
    const latestEntry = getLatestProgressEntry(activeGroup.value?.progressData);
    if (latestEntry) {
        quantity.value = latestEntry.quantity || 1;
        selectedReason.value = latestEntry.reason || "";
        comment.value = latestEntry.comment || "";
    }
});

watch(() => activeGroup.value?.progressData, (newProgressData) => {
    const latestEntry = getLatestProgressEntry(newProgressData);
    if (latestEntry) {
        quantity.value = latestEntry.quantity || 1;
        selectedReason.value = latestEntry.reason || "";
        comment.value = latestEntry.comment || "";
    }
});

const handleSave = () => {
    if (!selectedReason.value) {
        alert('Please select a reason');
        return;
    }

    const data = {
        reason: selectedReason.value,
        quantity: quantity.value,
        comment: comment.value
    };

    updateGroupMortalityData(data);

    if (activeTank.value) {
        navigateToTankView(activeTank.value.key);
    }
};

const handleBack = () => {
    if (activeTank.value) {
        navigateToTankView(activeTank.value.key);
    }
};

const handleNoMortality = () => {
    const data = {
        reason: "No mortality",
        quantity: 0,
        comment: comment.value || "No dead fish recorded for this group"
    };

    updateGroupMortalityData(data);

    if (activeTank.value) {
        navigateToTankView(activeTank.value.key);
    }
};
</script>

<template>
    <div class="h-full flex flex-col justify-between mt-5 gap-3 pb-5 mb-10">
        <div>
            <!-- Header -->
            <div class="flex gap-2 items-center pb-1 border-b border-secondary">
                <h1 class="py-2 text-3xl font-bold text-gray-700">
                    {{ activeTank?.label + " - " + activeGroup?.name }}
                </h1>
                <div class="flex items-center">
          <span
              v-for="flag of activeGroup?.tags"
              :key="flag.title"
              class="inline-flex mr-2 text-white text-xs p-1 rounded-md font-bold"
              :style="{ backgroundColor: flag.backgroundColor }"
          >
            {{ flag.title }}
          </span>
                </div>
            </div>

            <div class="flex flex-col gap-10 mt-10 relative">
                <!-- Reason and Quantity Selection -->
                <div class="flex flex-col md:flex-row gap-5">
                    <!-- Reason Selection -->
                    <div class="w-full md:w-1/2">
                        <h2 class="text-gray-700 text-xl font-bold">Select a reason:</h2>
                        <div class="flex gap-5 flex-col mt-3">
                            <button
                                v-for="reason in mortalityReasons"
                                :key="reason"
                                @click="handleReason(reason)"
                                class="px-4 border py-2 min-w-[200px]"
                                :class="{
                  'bg-primary': selectedReason === reason,
                  'text-primary': !selectedReason || selectedReason !== reason,
                  'text-white': selectedReason === reason,
                  'border-gray-600': !selectedReason || selectedReason !== reason,
                }"
                            >
                                {{ reason }}
                            </button>
                        </div>

                    </div>

                    <!-- Quantity Input -->
                    <div class="w-full md:w-1/2">
                        <h2 class="text-gray-700 text-xl font-bold">Insert a number:</h2>
                        <div class="flex items-center mt-3">
                            <button
                                class="px-4 py-2 border border-primary font-bold bg-primary text-white"
                                @click="decreaseQuantity"
                            >
                                -
                            </button>
                            <input
                                type="number"
                                v-model.number="quantity"
                                class="appearance-none w-full border border-primary px-4 py-2 text-center"
                            />
                            <button
                                class="px-4 py-2 border border-primary font-bold bg-primary text-white"
                                @click="increaseQuantity"
                            >
                                +
                            </button>
                        </div>

                        <!-- Statistics -->
                        <div class="flex flex-col gap-3 mt-5">
                            <strong>
                                Current total number of fish:
                                <span class="text-primary">{{ totalFishCount }}</span>
                            </strong>
                            <strong>
                                All time mortality rate:
                                <span class="text-primary">{{ mortalityRate }}%</span>
                            </strong>
                        </div>
                    </div>
                </div>

                <!-- Comment Section -->
                <div>
                    <h2 class="text-gray-700 text-xl font-bold">
                        Leave a comment (optional):
                    </h2>
                    <textarea
                        v-model="comment"
                        class="w-full mt-5 p-2 border border-secondary"
                        rows="6"
                    ></textarea>
                </div>
            </div>
        </div>

        <!-- Footer Actions -->
        <div class="mt-auto">
            <FooterActions>
                <template #start>
                    <button
                        @click="handleBack"
                        class="w-full"
                    >
                        Back
                    </button>
                </template>
                <template #middle>
                    <button
                        @click="handleNoMortality"
                        class="w-full"
                    >
                        Record no mortality
                    </button>
                </template>
                <template #end>
                    <button
                        @click="handleSave"
                        class="w-full"
                        :disabled="!selectedReason"
                    >
                        Save
                    </button>
                </template>
            </FooterActions>
        </div>
    </div>
</template>
